import React from "react";
import { Navigate } from "react-router-dom";
import { useProfissionalAtual } from "@/contexts/ProfissionalContext";
import type { Modulo } from "@/hooks/useTeamPermissions";

type Role = "owner" | "admin" | "profissional" | "recepcionista" | "sem_acesso";
type Acao = "ver" | "criar" | "editar" | "excluir";

interface RoleGuardProps {
  allow: Role[];
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Bloqueia acesso a rotas para papéis não autorizados.
 * Enquanto o perfil carrega, evita renderizar para não vazar conteúdo.
 */
export function RoleGuard({ allow, children, redirectTo = "/home" }: RoleGuardProps) {
  const { modeloAcesso, isLoading } = useProfissionalAtual();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Carregando...</div>
      </div>
    );
  }

  if (!allow.includes(modeloAcesso as Role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

export function PermissionGuard({
  modulo,
  acao = "ver",
  children,
  redirectTo = "/home",
}: {
  modulo: Modulo;
  acao?: Acao;
  children: React.ReactNode;
  redirectTo?: string;
}) {
  const { isLoading, podeVer, podeCriar, podeEditar, podeExcluir } = useProfissionalAtual();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Carregando...</div>
      </div>
    );
  }

  const allowed =
    acao === "ver" ? podeVer(modulo) :
    acao === "criar" ? podeCriar(modulo) :
    acao === "editar" ? podeEditar(modulo) :
    acao === "excluir" ? podeExcluir(modulo) : false;

  if (!allowed) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
